/**
 * airan-ntn — Canvas Renderer
 * Renders satellite orbits, UEs, ground stations, KPI charts
 */

import { SimState, Satellite, UserEquipment, GroundStation, RoundMetrics, SimConfig, Vec3 } from './types.js';
import { SAT_COLORS, UE_PALETTE, BS_COLORS, SNR_THRESHOLDS, snrColor, elevationColor, stateColor } from './config.js';
import { EARTH_RADIUS } from './config.js';
import { vec3Len, vec3Sub, vec3Norm, vec3Dot } from './types.js';
import { ecefToLla } from './orbital.js';

const TRAIL_MAX = 50;
const ORBIT_SEGMENTS = 64;

// ──────────────────────────────────────────────────────────────────────────────
// Coordinate Projection (ECEF to Canvas)
// ──────────────────────────────────────────────────────────────────────────────

export interface ViewState {
  center: { x: number; y: number }; // Canvas center
  scale: number;                     // Pixels per meter
  rotation: number;                  // Radians (Y-axis rotation)
  pitch: number;                     // Radians (X-axis tilt)
  isDragging: boolean;               // Mouse drag state
  lastMousePos: { x: number; y: number } | null;
}

export function createViewState(canvas: HTMLCanvasElement): ViewState {
  return {
    center: { x: canvas.width / 2, y: canvas.height / 2 },
    scale: 0.0001,
    rotation: 0,
    pitch: 0.3,
    isDragging: false,
    lastMousePos: null
  };
}

export function projectEcef(pos: Vec3, view: ViewState): { x: number; y: number; z: number } {
  // Simple orthographic projection with rotation
  const cosR = Math.cos(view.rotation);
  const sinR = Math.sin(view.rotation);
  const cosP = Math.cos(view.pitch);
  const sinP = Math.sin(view.pitch);

  // Rotate around Z, then tilt (pitch)
  const x1 = pos.x * cosR - pos.y * sinR;
  const y1 = pos.x * sinR + pos.y * cosR;
  const z1 = pos.z;

  const y2 = y1 * cosP - z1 * sinP;
  const z2 = y1 * sinP + z1 * cosP;

  return {
    x: view.center.x + x1 * view.scale,
    y: view.center.y - y2 * view.scale,
    z: z2 * view.scale
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Main Renderer
// ──────────────────────────────────────────────────────────────────────────────

export function renderTopology(
  ctx: CanvasRenderingContext2D,
  state: SimState,
  view: ViewState,
  showOrbits: boolean = true,
  showISL: boolean = true,
  showTrails: boolean = true
): void {
  const { satellites, groundStations, ues } = state;
  const { width, height } = ctx.canvas;

  // Clear
  ctx.fillStyle = '#0a0a1a';
  ctx.fillRect(0, 0, width, height);

  // Draw Earth
  drawEarth(ctx, view);

  // Draw orbital paths
  if (showOrbits) {
    drawOrbitalPaths(ctx, satellites, view);
  }

  // Draw ISL links
  if (showISL) {
    drawISLLinks(ctx, satellites, view);
  }

  // Draw feeder links
  drawFeederLinks(ctx, satellites, groundStations, view);

  // Draw ground stations
  drawGroundStations(ctx, groundStations, view);

  // Draw satellites
  drawSatellites(ctx, satellites, view);

  // Draw UE trails
  if (showTrails) {
    drawUETrails(ctx, ues, view);
  }

  // Draw UEs
  drawUEs(ctx, ues, view);

  // Draw legend
  drawLegend(ctx, view);
}

// ──────────────────────────────────────────────────────────────────────────────
// Individual Draw Functions
// ──────────────────────────────────────────────────────────────────────────────

function drawEarth(ctx: CanvasRenderingContext2D, view: ViewState): void {
  const earthPos = projectEcef({ x: 0, y: 0, z: 0 }, view);
  const earthRadius = EARTH_RADIUS * view.scale;

  // Earth glow
  const gradient = ctx.createRadialGradient(
    earthPos.x, earthPos.y, 0,
    earthPos.x, earthPos.y, earthRadius * 1.5
  );
  gradient.addColorStop(0, '#1a3a5c');
  gradient.addColorStop(0.5, '#0a1a3a');
  gradient.addColorStop(1, '#000000');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(earthPos.x, earthPos.y, earthRadius * 1.5, 0, Math.PI * 2);
  ctx.fill();

  // Earth sphere base
  ctx.fillStyle = '#0d1f3a';
  ctx.beginPath();
  ctx.arc(earthPos.x, earthPos.y, earthRadius, 0, Math.PI * 2);
  ctx.fill();

  // Draw 3D wireframe sphere with lat/lon lines that rotate with view
  drawWireframeSphere(ctx, view, earthRadius, earthPos);
}

function drawWireframeSphere(
  ctx: CanvasRenderingContext2D,
  view: ViewState,
  radius: number,
  center: { x: number; y: number; z: number }
): void {
  ctx.strokeStyle = '#2a5a8c';
  ctx.lineWidth = 0.8;
  ctx.globalAlpha = 0.6;

  // Latitude lines (parallels) - drawn with proper 3D rotation
  for (let lat = -75; lat <= 75; lat += 15) {
    const points: { x: number; y: number }[] = [];
    for (let lon = -180; lon <= 180; lon += 5) {
      const pos = llaToEcefVec(lat, lon, EARTH_RADIUS);
      const proj = projectEcef(pos, view);
      points.push({ x: proj.x, y: proj.y });
    }
    if (points.length > 1) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      ctx.stroke();
    }
  }

  // Longitude lines (meridians)
  for (let lon = -180; lon <= 180; lon += 30) {
    const points: { x: number; y: number }[] = [];
    for (let lat = -85; lat <= 85; lat += 5) {
      const pos = llaToEcefVec(lat, lon, EARTH_RADIUS);
      const proj = projectEcef(pos, view);
      points.push({ x: proj.x, y: proj.y });
    }
    if (points.length > 1) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      ctx.stroke();
    }
  }

  // Equator (thicker)
  ctx.strokeStyle = '#4a90d9';
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.8;
  const eqPoints: { x: number; y: number }[] = [];
  for (let lon = -180; lon <= 180; lon += 5) {
    const pos = llaToEcefVec(0, lon, EARTH_RADIUS);
    const proj = projectEcef(pos, view);
    eqPoints.push({ x: proj.x, y: proj.y });
  }
  ctx.beginPath();
  ctx.moveTo(eqPoints[0].x, eqPoints[0].y);
  for (let i = 1; i < eqPoints.length; i++) ctx.lineTo(eqPoints[i].x, eqPoints[i].y);
  ctx.stroke();

  // Prime meridian
  ctx.strokeStyle = '#4a90d9';
  const pmPoints: { x: number; y: number }[] = [];
  for (let lat = -85; lat <= 85; lat += 5) {
    const pos = llaToEcefVec(lat, 0, EARTH_RADIUS);
    const proj = projectEcef(pos, view);
    pmPoints.push({ x: proj.x, y: proj.y });
  }
  ctx.beginPath();
  ctx.moveTo(pmPoints[0].x, pmPoints[0].y);
  for (let i = 1; i < pmPoints.length; i++) ctx.lineTo(pmPoints[i].x, pmPoints[i].y);
  ctx.stroke();

  ctx.globalAlpha = 1;
  ctx.setLineDash([]);
}

function llaToEcefVec(lat: number, lon: number, alt: number): Vec3 {
  const latRad = lat * Math.PI / 180;
  const lonRad = lon * Math.PI / 180;
  const N = EARTH_RADIUS / Math.sqrt(1 - 0.00669438 * Math.sin(latRad) ** 2);
  return {
    x: (N + alt) * Math.cos(latRad) * Math.cos(lonRad),
    y: (N + alt) * Math.cos(latRad) * Math.sin(lonRad),
    z: (N * (1 - 0.00669438) + alt) * Math.sin(latRad)
  };
}

function drawOrbitalPaths(ctx: CanvasRenderingContext2D, satellites: Satellite[], view: ViewState): void {
  if (satellites.length === 0) return;

  // Group by orbital plane
  const planes = new Map<number, Satellite[]>();
  for (const sat of satellites) {
    const raan = sat.orbitalElements.raan;
    const planeKey = Math.round(raan * 180 / Math.PI / 5) * 5;
    if (!planes.has(planeKey)) planes.set(planeKey, []);
    planes.get(planeKey)!.push(sat);
  }

  ctx.strokeStyle = '#004466';
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);

  for (const [, planeSats] of planes) {
    if (planeSats.length < 2) continue;
    planeSats.sort((a, b) => a.orbitalElements.trueAnomaly - b.orbitalElements.trueAnomaly);

    ctx.beginPath();
    for (let i = 0; i <= ORBIT_SEGMENTS; i++) {
      const frac = i / ORBIT_SEGMENTS;
      const anomaly = frac * Math.PI * 2;
      const a = planeSats[0].orbitalElements.semiMajorAxis;
      const inc = planeSats[0].orbitalElements.inclination;
      const raan = planeSats[0].orbitalElements.raan;

      // Compute position at this anomaly
      const xOrb = a * Math.cos(anomaly);
      const yOrb = a * Math.sin(anomaly);

      const cosR = Math.cos(raan), sinR = Math.sin(raan);
      const cosI = Math.cos(inc), sinI = Math.sin(inc);

      const x = xOrb * cosR - yOrb * sinR * cosI;
      const y = xOrb * sinR + yOrb * cosR * cosI;
      const z = yOrb * sinI;

      const proj = projectEcef({ x, y, z }, view);
      if (i === 0) ctx.moveTo(proj.x, proj.y);
      else ctx.lineTo(proj.x, proj.y);
    }
    ctx.closePath();
    ctx.stroke();
  }

  ctx.setLineDash([]);
}

function drawISLLinks(ctx: CanvasRenderingContext2D, satellites: Satellite[], view: ViewState): void {
  ctx.strokeStyle = '#00ffff';
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.6;

  const drawn = new Set<string>();
  for (const sat of satellites) {
    for (const link of sat.islLinks) {
      const key = `${Math.min(sat.id, link.neighborId)}-${Math.max(sat.id, link.neighborId)}`;
      if (drawn.has(key)) continue;
      drawn.add(key);

      const neighbor = satellites.find(s => s.id === link.neighborId);
      if (!neighbor) continue;

      const p1 = projectEcef(sat.position, view);
      const p2 = projectEcef(neighbor.position, view);

      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
  }

  ctx.globalAlpha = 1;
  ctx.setLineDash([]);
}

function drawFeederLinks(
  ctx: CanvasRenderingContext2D,
  satellites: Satellite[],
  groundStations: GroundStation[],
  view: ViewState
): void {
  ctx.strokeStyle = '#ffaa00';
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.8;

  for (const sat of satellites) {
    if (!sat.feederLink || !sat.feederLink.active) continue;
    const gs = groundStations.find(g => g.id === sat.feederLink!.groundStationId);
    if (!gs) continue;

    const p1 = projectEcef(sat.position, view);
    const p2 = projectEcef(gs.position, view);

    // Dashed line for feeder
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.globalAlpha = 1;
}

function drawGroundStations(ctx: CanvasRenderingContext2D, gss: GroundStation[], view: ViewState): void {
  for (const gs of gss) {
    const proj = projectEcef(gs.position, view);
    const size = 8;

    // Tower
    ctx.fillStyle = '#666';
    ctx.fillRect(proj.x - 2, proj.y - size, 4, size);

    // Antennas
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    for (let i = 0; i < gs.antennas; i++) {
      const angle = (i / gs.antennas) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(proj.x, proj.y - size);
      ctx.lineTo(proj.x + Math.cos(angle) * 6, proj.y - size - Math.sin(angle) * 6);
      ctx.stroke();
    }

    // Label
    ctx.fillStyle = '#fff';
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(gs.name, proj.x, proj.y - size - 10);
  }
}

function drawSatellites(ctx: CanvasRenderingContext2D, sats: Satellite[], view: ViewState): void {
  for (const sat of sats) {
    const proj = projectEcef(sat.position, view);

    // Satellite body
    ctx.fillStyle = sat.color;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, 4, 0, Math.PI * 2);
    ctx.fill();

    // Glow
    ctx.shadowColor = sat.color;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // State indicator
    ctx.fillStyle = sat.state === 'ECLIPSE' ? '#7b4fcf' :
                    sat.state === 'FEEDER_OUTAGE' ? '#cc2233' :
                    sat.state === 'AUTONOMOUS' ? '#e07030' :
                    '#008855';
    ctx.beginPath();
    ctx.arc(proj.x + 5, proj.y - 5, 3, 0, Math.PI * 2);
    ctx.fill();

    // Solar panels (simple lines)
    ctx.strokeStyle = sat.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(proj.x - 8, proj.y);
    ctx.lineTo(proj.x - 12, proj.y - 4);
    ctx.moveTo(proj.x + 8, proj.y);
    ctx.lineTo(proj.x + 12, proj.y + 4);
    ctx.stroke();

    // Label
    ctx.fillStyle = '#fff';
    ctx.font = '8px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(sat.name, proj.x, proj.y + 14);
  }
}

function drawUETrails(ctx: CanvasRenderingContext2D, ues: UserEquipment[], view: ViewState): void {
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.4;

  for (const ue of ues) {
    if (ue.trail.length < 2) continue;
    const color = ue.color;
    ctx.strokeStyle = color;

    ctx.beginPath();
    const first = projectEcef(ue.trail[0], view);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < ue.trail.length; i++) {
      const p = projectEcef(ue.trail[i], view);
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  ctx.globalAlpha = 1;
}

function drawUEs(ctx: CanvasRenderingContext2D, ues: UserEquipment[], view: ViewState): void {
  for (const ue of ues) {
    const proj = projectEcef(ue.position, view);

    // UE circle
    ctx.fillStyle = ue.selected ? '#ffff00' : ue.color;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, ue.selected ? 5 : 3, 0, Math.PI * 2);
    ctx.fill();

    // SNR indicator
    const snrCol = snrColor(ue.snr);
    ctx.strokeStyle = snrCol;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(proj.x, proj.y, ue.selected ? 7 : 5, 0, Math.PI * 2);
    ctx.stroke();

    // Velocity arrow
    if (ue.velocity.x !== 0 || ue.velocity.y !== 0) {
      const speed = Math.sqrt(ue.velocity.x ** 2 + ue.velocity.y ** 2);
      if (speed > 0.01) {
        const velProj = projectEcef(
          { x: ue.position.x + ue.velocity.x * 1000, y: ue.position.y + ue.velocity.y * 1000, z: ue.position.z },
          view
        );
        ctx.strokeStyle = ue.color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(proj.x, proj.y);
        ctx.lineTo(velProj.x, velProj.y);
        ctx.stroke();

        // Arrowhead
        const angle = Math.atan2(velProj.y - proj.y, velProj.x - proj.x);
        ctx.beginPath();
        ctx.moveTo(velProj.x, velProj.y);
        ctx.lineTo(velProj.x - Math.cos(angle - 0.5) * 5, velProj.y - Math.sin(angle - 0.5) * 5);
        ctx.lineTo(velProj.x - Math.cos(angle + 0.5) * 5, velProj.y - Math.sin(angle + 0.5) * 5);
        ctx.closePath();
        ctx.fillStyle = ue.color;
        ctx.fill();
      }
    }

    // Serving satellite line
    if (ue.servingSatId !== null) {
      const sat = state?.satellites.find(s => s.id === ue.servingSatId);
      if (sat) {
        const satProj = projectEcef(sat.position, view);
        ctx.strokeStyle = snrCol;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(proj.x, proj.y);
        ctx.lineTo(satProj.x, satProj.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }
}

// Need to access state globally for UE serving satellite line
let state: SimState | null = null;
export function setRenderState(s: SimState) { state = s; }

function drawLegend(ctx: CanvasRenderingContext2D, view: ViewState): void {
  const padding = 10;
  const x = view.center.x * 2 - 200;
  const y = padding;

  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(x - 5, y - 5, 190, 120);

  ctx.fillStyle = '#fff';
  ctx.font = '10px monospace';
  ctx.textAlign = 'left';

  const items = [
    { color: '#00ffff', label: 'ISL Links' },
    { color: '#ffaa00', label: 'Feeder Links' },
    { color: '#008855', label: 'SNR Good' },
    { color: '#c89000', label: 'SNR Medium' },
    { color: '#cc2233', label: 'SNR Poor' },
    { color: '#ffff00', label: 'Selected UE' }
  ];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    ctx.fillStyle = item.color;
    ctx.fillRect(x, y + i * 18, 12, 12);
    ctx.fillStyle = '#fff';
    ctx.fillText(item.label, x + 18, y + i * 18 + 10);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// KPI Charts
// ──────────────────────────────────────────────────────────────────────────────

export function renderCharts(
  ctx: CanvasRenderingContext2D,
  history: RoundMetrics[],
  canvasWidth: number,
  canvasHeight: number
): void {
  if (history.length === 0) return;

  const margin = 40;
  const chartWidth = canvasWidth - margin * 2;
  const chartHeight = 120;
  const startY = canvasHeight - chartHeight - margin;

  // Draw loss chart
  drawChart(ctx, history, 'globalLoss', margin, startY, chartWidth, chartHeight, '#ff4444', 'Loss');
  drawChart(ctx, history, 'globalAccuracy', margin, startY - chartHeight - 10, chartWidth, chartHeight, '#44ff44', 'Accuracy (%)');
  drawChart(ctx, history, 'avgSNR', margin, startY - 2 * (chartHeight + 10), chartWidth, chartHeight, '#44aaff', 'Avg SNR (dB)');
}

function drawChart(
  ctx: CanvasRenderingContext2D,
  history: RoundMetrics[],
  metric: keyof RoundMetrics,
  x: number,
  y: number,
  width: number,
  height: number,
  color: string,
  label: string
): void {
  if (history.length < 2) return;

  // Background
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(x, y, width, height);

  // Axes
  ctx.strokeStyle = '#444';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();

  // Data
  const values = history.map(h => h[metric] as number);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;

  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();

  for (let i = 0; i < values.length; i++) {
    const px = x + (i / (values.length - 1)) * width;
    const py = y + height - ((values[i] - minVal) / range) * height;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();

  // Label
  ctx.fillStyle = color;
  ctx.font = '10px monospace';
  ctx.fillText(`${label}: ${values[values.length - 1].toFixed(2)}`, x + 5, y + 15);
}

// ──────────────────────────────────────────────────────────────────────────────
// SNR Bars
// ──────────────────────────────────────────────────────────────────────────────

export function renderSNRBars(
  ctx: CanvasRenderingContext2D,
  ues: UserEquipment[],
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const barWidth = width / Math.max(1, ues.length);
  const maxSNR = 30;

  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(x, y, width, height);

  for (let i = 0; i < ues.length; i++) {
    const ue = ues[i];
    const barHeight = Math.max(0, (ue.snr / maxSNR) * height);
    const bx = x + i * barWidth + 1;
    const by = y + height - barHeight;

    ctx.fillStyle = snrColor(ue.snr);
    ctx.fillRect(bx, by, barWidth - 2, barHeight);
  }

  // Labels
  ctx.fillStyle = '#fff';
  ctx.font = '8px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('UE SNR', x + width / 2, y - 5);
}